import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="organization-form"
export default class extends Controller {
  static targets = ["parentSelect", "form", "dspPartnerSelect", "dashgoApiKeySection", "dashgoApiKeySelect", "dspRow"]
  static values = { dashgoPartnerId: String, fugaPartnerId: String }

  connect() {
    this.toggleParentSelect()
    this.toggleDashgoApiKey()
    this.toggleFugaDsps()

    // Add form submission handler for validation
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("submit", this.validateForm.bind(this))
    }
  }

  toggleParentSelect() {
    const artistRadio = this.element.querySelector('input[value="artist"]')
    const labelRadio = this.element.querySelector('input[value="label"]')

    if (artistRadio && artistRadio.checked) {
      if (this.hasParentSelectTarget) {
        this.parentSelectTarget.classList.remove("hidden")
      }
    } else {
      if (this.hasParentSelectTarget) {
        this.parentSelectTarget.classList.add("hidden")
      }
    }
  }

  // The DashGo API key only applies to organizations delivered through DashGo,
  // so hide it (and drop any selected key) for every other partner.
  toggleDashgoApiKey() {
    if (!this.hasDashgoApiKeySectionTarget) return

    const selectedPartnerId = this.hasDspPartnerSelectTarget ? this.dspPartnerSelectTarget.value : ""
    const isDashgo = selectedPartnerId !== "" && selectedPartnerId === this.dashgoPartnerIdValue

    this.dashgoApiKeySectionTarget.classList.toggle("hidden", !isDashgo)

    if (!isDashgo && this.hasDashgoApiKeySelectTarget) {
      this.dashgoApiKeySelectTarget.value = ""
    }
  }

  // A DSP with no FUGA id cannot be delivered through FUGA, so it is hidden -
  // and unchecked - while the FUGA partner is selected.
  toggleFugaDsps() {
    if (!this.hasDspRowTarget) return

    const selectedPartnerId = this.hasDspPartnerSelectTarget ? this.dspPartnerSelectTarget.value : ""
    const isFuga = selectedPartnerId !== "" && selectedPartnerId === this.fugaPartnerIdValue

    this.dspRowTargets.forEach(row => {
      const onFuga = row.dataset.onFuga === "true"
      const hide = isFuga && !onFuga

      row.classList.toggle("hidden", hide)

      if (hide) {
        const checkbox = row.querySelector('input[type="checkbox"]')
        if (checkbox) checkbox.checked = false
      }
    })
  }

  validateForm(event) {
    const errors = this.collectValidationErrors()

    if (errors.length > 0) {
      event.preventDefault()
      this.showValidationErrors(errors)
      return false
    }

    return true
  }

  collectValidationErrors() {
    const errors = []
    const form = this.hasFormTarget ? this.formTarget : this.element

    // Title validation
    const titleInput = form.querySelector('[name="organization[title]"]')
    if (titleInput && this.isFieldEmpty(titleInput)) {
      errors.push("Organization name is required")
    }

    // Organization type validation (only for new organizations)
    const organizationTypeRadios = form.querySelectorAll('[name="organization[organization_type]"]')
    if (organizationTypeRadios.length > 0) {
      const isTypeSelected = Array.from(organizationTypeRadios).some(radio => radio.checked)
      if (!isTypeSelected) {
        errors.push("Organization type is required")
      }
    }

    // Service fee validation
    const serviceFeeInput = form.querySelector('[name="organization[service_fee]"]')
    if (serviceFeeInput && this.isFieldEmpty(serviceFeeInput)) {
      errors.push("Service fee is required")
    }

    // DSP partner validation - exactly one partner is required
    const dspPartnerSelect = form.querySelector('[name="organization[dsp_partner_id]"]')
    if (dspPartnerSelect && this.isFieldEmpty(dspPartnerSelect)) {
      errors.push("DSP Partner is required")
    }

    // DSP validation - at least one DSP must be selected
    const dspCheckboxes = form.querySelectorAll('[name="organization[dsp_ids][]"]:checked')
    if (dspCheckboxes.length === 0) {
      errors.push("Please select at least one DSP for distribution")
    }

    return errors
  }

  isFieldEmpty(input) {
    if (!input) return true

    const value = input.value.trim()

    // Special handling for select elements
    if (input.tagName.toLowerCase() === 'select') {
      return !value || value === ''
    }

    return !value
  }

  showValidationErrors(errors) {
    // Remove any existing error display
    this.removeExistingErrors()

    const form = this.hasFormTarget ? this.formTarget : this.element

    // Create error summary
    const errorContainer = document.createElement('div')
    errorContainer.className = 'rounded-md bg-red-50 p-4 mb-6'
    errorContainer.id = 'client-validation-errors'

    const errorContent = `
      <div class="flex">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">
            There ${errors.length === 1 ? 'is' : 'are'} ${errors.length} error${errors.length === 1 ? '' : 's'} with your submission
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <ul class="list-disc pl-5 space-y-1">
              ${errors.map(error => `<li>${error}</li>`).join('')}
            </ul>
          </div>
        </div>
      </div>
    `

    errorContainer.innerHTML = errorContent

    // Insert error container at the top of the form
    form.insertBefore(errorContainer, form.firstChild)

    // Scroll to the top of the form to show errors
    errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

    // Add red borders to empty required fields
    this.highlightErrorFields()
  }

  removeExistingErrors() {
    const existingErrors = document.getElementById('client-validation-errors')
    if (existingErrors) {
      existingErrors.remove()
    }

    const form = this.hasFormTarget ? this.formTarget : this.element

    // Remove error styling from regular fields
    form.querySelectorAll('.border-red-500').forEach(field => {
      field.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
      field.classList.add('border-gray-300', 'focus:border-indigo-500', 'focus:ring-indigo-500')
    })

    // Remove error styling from DSP selector block
    const dspBlock = form.querySelector('[data-controller="dsp-selector"]')
    if (dspBlock) {
      const dspBorder = dspBlock.querySelector('.border')
      if (dspBorder) {
        dspBorder.classList.remove('border-red-500')
        dspBorder.classList.add('border-gray-300')
      }
    }
  }

  highlightErrorFields() {
    const form = this.hasFormTarget ? this.formTarget : this.element

    // Highlight title field if empty
    const titleInput = form.querySelector('[name="organization[title]"]')
    if (titleInput && this.isFieldEmpty(titleInput)) {
      titleInput.classList.remove('border-gray-300', 'focus:border-indigo-500', 'focus:ring-indigo-500')
      titleInput.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
    }

    // Highlight service fee field if empty
    const serviceFeeInput = form.querySelector('[name="organization[service_fee]"]')
    if (serviceFeeInput && this.isFieldEmpty(serviceFeeInput)) {
      serviceFeeInput.classList.remove('border-gray-300', 'focus:border-indigo-500', 'focus:ring-indigo-500')
      serviceFeeInput.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
    }

    // Highlight DSP partner select if nothing chosen
    const dspPartnerSelect = form.querySelector('[name="organization[dsp_partner_id]"]')
    if (dspPartnerSelect && this.isFieldEmpty(dspPartnerSelect)) {
      dspPartnerSelect.classList.remove('border-gray-300', 'focus:border-indigo-500', 'focus:ring-indigo-500')
      dspPartnerSelect.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
    }

    // Highlight DSP selector if no DSPs selected
    const dspCheckboxes = form.querySelectorAll('[name="organization[dsp_ids][]"]:checked')
    if (dspCheckboxes.length === 0) {
      const dspBlock = form.querySelector('[data-controller="dsp-selector"]')
      if (dspBlock) {
        const dspBorder = dspBlock.querySelector('.border')
        if (dspBorder) {
          dspBorder.classList.remove('border-gray-300')
          dspBorder.classList.add('border-red-500')
        }
      }
    }
  }
}
